import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (lang: string) => void;
  t: (text: string) => string;
  getLocalizedPath: (path: string) => string;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'preferred_language';

const FALLBACK_NON_EN_LANGS = ['de', 'tr', 'ru', 'es', 'it', 'nl'];

const isExcludedFromLanguagePrefix = (pathname: string) => {
  const first = pathname.split('/')[1];
  return first === 'admin' || first === 'owner' || first === 'demo';
};

// Shared language codes cache - used by both LanguageContext and App.tsx
// This prevents duplicate queries to the languages table
let validLanguageCodes: string[] | null = null;
let loadPromise: Promise<string[]> | null = null;

export const loadValidLanguages = async (force = false): Promise<string[]> => {
  if (validLanguageCodes && !force) return validLanguageCodes;
  if (!force) return FALLBACK_NON_EN_LANGS;
  
  if (loadPromise) return loadPromise;
  
  loadPromise = (async () => {
    try {
      const { languagesService } = await import('@/services/languages');
      const data = await languagesService.getActive();
      
      validLanguageCodes = data?.map(l => l.code) || FALLBACK_NON_EN_LANGS;
      return validLanguageCodes;
    } catch {
      return FALLBACK_NON_EN_LANGS;
    }
  })();
  
  return loadPromise;
};

// Initialize on module load REMOVED to prevent critical path blocking
// loadValidLanguages();

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [validLangs, setValidLangs] = useState<string[]>(FALLBACK_NON_EN_LANGS);
  
  // Load valid languages on mount without artificial delay
  useEffect(() => {
    loadValidLanguages(true).then(setValidLangs);
  }, []);
  
  // Initialize language from URL param - URL is the source of truth
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    const pathLang = location.pathname.split('/')[1];
    // Initial check with default languages, will be updated when validLangs loads
    const defaultLangs = FALLBACK_NON_EN_LANGS;
    if (pathLang && defaultLangs.includes(pathLang)) {
      localStorage.setItem(STORAGE_KEY, pathLang);
      return pathLang;
    }

    // No prefix: respect saved preference (so NL doesn't "disappear" back to /blog)
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && saved !== 'en' && defaultLangs.includes(saved)) {
      return saved;
    }

    return 'en';
  });
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);

  // Sync language with URL parameter - only if URL explicitly has a language prefix
  // Don't reset to English just because URL has no prefix - respect localStorage preference
  useEffect(() => {
    const pathLang = location.pathname.split('/')[1];
    if (pathLang && validLangs.includes(pathLang)) {
      // URL has explicit language prefix - use it
      if (pathLang !== currentLanguage) {
        setCurrentLanguage(pathLang);
        localStorage.setItem(STORAGE_KEY, pathLang);
      }
    }
    // If no language in URL, keep the current language from state/localStorage
    // Don't force reset to English
  }, [location.pathname, validLangs]);

  // Enforce language prefix in URL for non-English languages.
  // This prevents cases like currentLanguage='nl' while URL stays '/blog'.
  // IMPORTANT: Never create URLs with trailing slashes!
  useEffect(() => {
    if (currentLanguage === 'en') return;
    if (isExcludedFromLanguagePrefix(location.pathname)) return;

    // Check if URL already has the correct language prefix
    const pathWithoutTrailing = location.pathname.endsWith('/') && location.pathname !== '/'
      ? location.pathname.slice(0, -1)
      : location.pathname;
    
    const hasPrefix =
      pathWithoutTrailing === `/${currentLanguage}` ||
      pathWithoutTrailing.startsWith(`/${currentLanguage}/`);

    if (hasPrefix) return;

    // Build new path WITHOUT trailing slash
    const cleanPath = pathWithoutTrailing === '/' ? '' : pathWithoutTrailing;
    const nextPath = `/${currentLanguage}${cleanPath}${location.search}${location.hash}`;
    navigate(nextPath, { replace: true });
  }, [currentLanguage, location.pathname, location.search, location.hash, navigate]);

  // Load translations when language changes
  useEffect(() => {
    if (currentLanguage !== 'en') {
      loadTranslations();
    } else {
      setTranslations({});
      setIsLoading(false);
    }
  }, [currentLanguage]);

  const loadTranslations = async () => {
    try {
      const cachedData = localStorage.getItem(`translations_${currentLanguage}`);
      const cacheTimestamp = localStorage.getItem(`translations_${currentLanguage}_timestamp`);
      const CACHE_DURATION = 24 * 60 * 60 * 1000;

      if (cachedData && cacheTimestamp) {
        const now = new Date().getTime();
        const age = now - parseInt(cacheTimestamp);
        
        if (age < CACHE_DURATION) {
          try {
            const parsed = JSON.parse(cachedData);
            setTranslations(parsed);
            setIsLoading(false);
            fetchTranslationsFromSupabase(true);
            return;
          } catch (e) {
            console.error('Error parsing cached translations', e);
          }
        }
      }

      setIsLoading(true);
      await fetchTranslationsFromSupabase(false);
    } catch (error) {
      console.error('Error loading translations:', error);
      setIsLoading(false);
    }
  };

  const fetchTranslationsFromSupabase = async (isBackground: boolean) => {
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const translationMap: Record<string, string> = {};
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;
      
      // Load all translations with pagination to overcome 1000 row limit
      while (hasMore) {
        const { data, error } = await supabase
          .from('string_keys')
          .select(`
            source_string,
            translations!inner(translation_text)
          `)
          .eq('translations.language_code', currentLanguage)
          .range(from, from + pageSize - 1);

        if (error) {
          console.error('Error loading translations:', error);
          break;
        }
        
        if (!data || data.length === 0) {
          hasMore = false;
          break;
        }
        
        (data || []).forEach((item: any) => {
          const sourceKey = (item.source_string || '').trim();
          const translationText = item.translations?.[0]?.translation_text;
          
          if (sourceKey && translationText) {
            const trimmedTranslation = translationText.trim();
            if (trimmedTranslation) {
              translationMap[sourceKey] = trimmedTranslation;
            }
          }
        });
        
        if (data.length < pageSize) {
          hasMore = false;
        } else {
          from += pageSize;
        }
      }
      
      // Log removed

      
      // Only update state if we found translations
      if (Object.keys(translationMap).length > 0) {
        setTranslations(translationMap);
        
        // Update cache
        localStorage.setItem(`translations_${currentLanguage}`, JSON.stringify(translationMap));
        localStorage.setItem(`translations_${currentLanguage}_timestamp`, new Date().getTime().toString());
      }
    } catch (error) {
      console.error('Error fetching translations:', error);
    } finally {
      if (!isBackground) {
        setIsLoading(false);
      }
    }
  };

  const setLanguage = useCallback((lang: string) => {
    setCurrentLanguage(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  }, []);

  const t = useCallback((text: string): string => {
    if (currentLanguage === 'en') return text || "";
    if (!text) return "";
    
    const key = text.trim();
    // Use optional chaining for safety
    const translation = translations?.[key];
    
    return translation || text;
  }, [currentLanguage, translations]);

  // Generate localized path with language prefix
  const getLocalizedPath = useCallback((path: string): string => {
    // Don't add prefix for English (default language)
    if (currentLanguage === 'en') {
      return path;
    }
    // Add language prefix for other languages
    // Make sure path starts with /
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    
    // Fix: Avoid trailing slash for language root (e.g. return /de instead of /de/)
    if (cleanPath === '/') {
      return `/${currentLanguage}`;
    }

    return `/${currentLanguage}${cleanPath}`;
  }, [currentLanguage]);

  const value = React.useMemo(() => ({
    currentLanguage,
    setLanguage,
    t,
    getLocalizedPath,
    isLoading
  }), [currentLanguage, setLanguage, t, getLocalizedPath, isLoading]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
